import React, { useState, useRef } from 'react';
import Product from './Product';
import LimitedByDesign from "./LimitedByDesign";
import { FaTimes} from 'react-icons/fa';
import { FiSearch, FiGrid, FiList } from "react-icons/fi";
import { trackEvent } from "./utils/analytics";

const ProductList = ({ products = [], addToCart }) => {
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [sortOption, setSortOption] = useState("newest");
  const [openCollections, setOpenCollections] = useState(false);
  const [heelHeight, setHeelHeight] = useState('');
  const [isGalleryView, setIsGalleryView] = useState(false);
  const [ratingsMap] = useState({});

  const collections = [...new Set(products.map(p => p.brand))];

  const handleSizeChange = (e) => {
    const value = e.target.value;

    trackEvent({
      event_name: "select_filter",
      filter_type: "size",
      filter_value: value,
    });

    setSelectedSize(value);
  };

  const handleTypeChange = (e) => {
    const value = e.target.value;

    trackEvent({
      event_name: "select_filter",
      filter_type: "type",
      filter_value: value,
    });

    setSelectedType(value);
  };

  const handlePriceChange = (e) => {
    const rawValue = e.target.value;

    trackEvent({
      event_name: "select_filter",
      filter_type: "price",
      filter_value: rawValue,
    });

    const value = rawValue.split('-').map(Number);
    setPriceRange(value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleHeelHeightChange = (e) => {
    const value = e.target.value;

    trackEvent({
      event_name: "select_filter",
      filter_type: "heel_height",
      filter_value: value,
    });

    setHeelHeight(value);
  };

  const clearFilters = () => {
    setSelectedSize('');
    setSelectedType('');
    setPriceRange([0, 10000]);
    setSearchQuery('');
    setHeelHeight('');
  };

  const filteredProducts = products.filter((product) => {

    const matchesSize = selectedSize
      ? product.sizes.some(s => s.size === selectedSize && s.stock > 0)
      : true;

    const matchesType = selectedType
      ? product.category === selectedType
      : true;

    const matchesPrice =
      product.price >= priceRange[0] && product.price <= priceRange[1];

      const matchesSearch = (() => {
        if (!searchQuery) return true;

        const query = searchQuery.toLowerCase();

        // Direct matches
        const directMatch =
          product.name?.toLowerCase().includes(query) ||
          product.brand?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query) ||
          product.description?.toLowerCase().includes(query) ||
          product.styleID?.toLowerCase().includes(query) ||
          product.appearance?.primaryColor?.toLowerCase().includes(query);

        if (directMatch) return true;

        // Style family match
        const matchingProduct = products.find(
          p =>
            p.name?.toLowerCase().includes(query)
        );

        if (
          matchingProduct &&
          matchingProduct.styleID &&
          product.styleID === matchingProduct.styleID
        ) {
          return true;
        }

        return false;
      })();

    const matchesHeelHeight = heelHeight
      ? product.fit && product.fit.heelHeight === Number(heelHeight)
      : true;

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const productDate = new Date(product.createdAt);

    const isNewProduct = productDate > oneMonthAgo;

    const matchesCollection =
      collectionFilter === "all"
        ? true
        : collectionFilter === "new"
        ? isNewProduct
        : collectionFilter === "top"
        ? product.topPick === true
        : product.brand === collectionFilter;

    return (
      matchesSize &&
      matchesType &&
      matchesPrice &&
      matchesSearch &&
      matchesCollection &&
      matchesHeelHeight
    );

  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {

    if (sortOption === "low") {
      return a.price - b.price;
    }

    if (sortOption === "high") {
      return b.price - a.price;
    }

    if (sortOption === "newest") {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }

    if (sortOption === "rating") {
      const ratingA = ratingsMap[a._id]?.average || 0;
      const ratingB = ratingsMap[b._id]?.average || 0;

      return ratingB - ratingA;
    }

    return 0;

  });

  const heelHeights = [
    ...new Set(
      products
        .map(p => p.fit?.heelHeight)
        .filter(h => h !== undefined && h !== null)
    )
  ].sort((a, b) => a - b);

  const scrollRef = useRef();

  // eslint-disable-next-line no-unused-vars
  const scroll = (dir) => {
    scrollRef.current.scrollBy({
      left: dir === 'left' ? -300 : 300,
      behavior: 'smooth'
    });
  };

  return (
    <div className="product-list">
    <h2 className="product-list-title">The STILL Collection</h2>

    <LimitedByDesign />

      <div className="collection-nav">
        <div className="collection-top">
        <button onClick={() => setOpenCollections(!openCollections)}>
          Shop by Collection
        </button>

        <button
          className={collectionFilter === "all" ? "active" : ""}
          onClick={() => {
            trackEvent({
              event_name: "select_collection",
              collection_name: "all_products",
            });

            setCollectionFilter("all");
          }}
        >
          All Products
        </button>

        <button
          className={collectionFilter === "new" ? "active" : ""}
          onClick={() => {
            trackEvent({
              event_name: "select_collection",
              collection_name: "new_in",
            });

            setCollectionFilter("new");
          }}
        >
          New In
        </button>

        <button
          className={collectionFilter === "top" ? "active" : ""}
          onClick={() => {
            trackEvent({
              event_name: "select_collection",
              collection_name: "most_wanted",
            });

            setCollectionFilter("top");
          }}
        >
          Most Wanted
        </button>
      </div>

      {openCollections && (
        <div className="collection-wrapper">

          <div className="collection-dropdown" ref={scrollRef}>
            {collections.map((col) => (
              <div
                key={col}
                className="collection-card"
                onClick={() => {
                  trackEvent({
                    event_name: "select_collection",
                    collection_name: col,
                  });

                  setCollectionFilter(col);
                  setOpenCollections(false);
                }}
              >
                <img src={`/collections/${col.replace(/\s+/g,'')}.jpeg`} alt={col} />
                <p>{col}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>

      <div className="sort-container">
        <label>Sort By</label>
        <select
          value={sortOption}
          onChange={(e) => {
            trackEvent({
              event_name: "select_sort",
              sort_option: e.target.value,
            });

            setSortOption(e.target.value);
          }}
        >
          <option value="newest">Newest</option>
          <option value="rating">Rating: High → Low</option>
          <option value="low">Price: Low → High</option>
          <option value="high">Price: High → Low</option>
        </select>
      </div>

      {/* Floating Action Buttons */}
      <div className="fab-container">

        {/* Gallery / Details Toggle Button */}
        <button
          className="view-fab"
          onClick={() => setIsGalleryView(!isGalleryView)}
          title={isGalleryView ? "Show Details" : "Gallery View"}
        >
          {isGalleryView ? <FiList /> : <FiGrid />}
        </button>
        {/* Floating filter button */}
        <button
          className="filter-fab"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          {isFilterOpen ? <FaTimes /> : <FiSearch />}
        </button>
      </div>

      {/* Filter panel (popover) */}
      {isFilterOpen && (
        <div className="filter-panel">

        <h4 className="filter-title">Filters</h4>
          {/* Search Bar */}
          <div className="filter-bubble">
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Type name here..."
              className="filter-input"
            />
          </div>

          {/* Size Filter */}
          <div className="filter-bubble">
            <select
              value={selectedSize}
              onChange={handleSizeChange}
              className="filter-select"
            >
              <option value="">Size</option>
              {[...Array(9)].map((_, i) => {
                const size = (34 + i).toString();
                return <option key={size} value={size}>{size}</option>;
              })}
            </select>
          </div>

          {/* Type Filter */}
          <div className="filter-bubble">
            <select
              value={selectedType}
              onChange={handleTypeChange}
              className="filter-select"
            >
              <option value="">Type</option>
              <option value="Heels">Heels</option>
              <option value="Flats">Flats</option>
              <option value="Bellies">Bellies</option>
            </select>
          </div>

          {/* Price Filter */}
          <div className="filter-bubble">
            <select
              value={priceRange.join('-')}
              onChange={handlePriceChange}
              className="filter-select"
            >
              <option value="0-10000">Price</option>
              <option value="0-999">₹0 - ₹999</option>
              <option value="1000-1999">₹1000 - ₹1999</option>
              <option value="2000-2999">₹2000 - ₹2999</option>
              <option value="3000-10000">₹3000+</option>
            </select>
          </div>

          <div className="filter-bubble">
            <select
              value={heelHeight}
              onChange={handleHeelHeightChange}
              className="filter-select"
            >
              <option value="">Heel Height</option>

              {heelHeights.map((height) => (
                <option key={height} value={height}>
                  {height}" Heel
                </option>
              ))}

            </select>
          </div>

          {/* Clear Filters Button */}
          <div className="filter-actions">

            <button className="clear-filters" onClick={clearFilters}>
               Clear
            </button>

            <button
              className="apply-filters"
              onClick={() => {

                if (searchQuery) {
                  trackEvent({
                    event_name: "search",
                    search_term: searchQuery,
                  });
                }

                setIsFilterOpen(false);
              }}
            >
              Apply Filters
            </button>

          </div>
        </div>
      )}

      <div className="product-grid">
        {filteredProducts.length > 0 ? (
          sortedProducts.map((product, index) => (
            <Product key={product._id} product={product} addToCart={addToCart} isFirstProduct={index === 0} isGalleryView={isGalleryView}/>
          ))
        ) : (
          <p className="no-results">No products match the filters.</p>
        )}
      </div>
    </div>
  );
};

export default ProductList;
